import { client } from "@/client";
import { CustomToastComponent } from "@/components/shared";
import { useMutation } from "@apollo/client/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  FetchPurchasesOnTenderDocument,
  FetchTenderByIdDocument,
  FetchServicesOnTenderDocument,
  RemovePurchaseDocument,
  RemovePurchaseMutation,
  RemovePurchaseMutationVariables,
} from "@/graphql/__generated__/graphql";

interface RemovePurchaseOptions {
  disableToast?: boolean;
  disableRefetch?: boolean;
}

/**
 * Custom hook for removing a purchase from a tender and handling the necessary cache updates.
 * @param {RemovePurchaseOptions} options - Optional configuration options
 * @param {boolean} [options.disableToast=false] - Whether to disable the success toast
 * @param {boolean} [options.disableRefetch=false] - Whether to disable refetching queries and cache eviction.
 *   When true, prevents Apollo from automatically refetching dependent queries (like Services)
 *   that would be triggered by cache eviction. Use this during edit operations (delete-create pattern)
 *   to avoid showing intermediate states.
 * @returns {Object}
 */
export default function useRemovePurchaseMutationHook(options: RemovePurchaseOptions = {}): { removePurchaseMutation: any; loading: any; error: any; setDisableToast: (_value: boolean) => void } {
  const { disableToast: initialDisableToast = false } = options;
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Use ref to allow dynamic toast suppression
  const disableToastRef = useRef(initialDisableToast);

  const setDisableToast = (value: boolean) => {
    disableToastRef.current = value;
  };

  const [removePurchaseMutation, { loading, error }] = useMutation<RemovePurchaseMutation, RemovePurchaseMutationVariables>(
    RemovePurchaseDocument, {
      onCompleted: (data) => {
        if (!disableToastRef.current) {
          toast(CustomToastComponent, {
            toastId: "purchase-removed-success",
            data: {
              title: t("toasts.purchaseremoved"),
              content: t("toasts.purchaseremovedsuccessfully"),
              type: "success",
            },
          });
        }

        // Only evict from cache if refetch is NOT disabled
        // When disableRefetch is true (e.g., during edit operations), we skip eviction
        // to prevent Apollo from automatically refetching dependent queries (like Services)
        // before the operation completes. The cache will be updated by subsequent mutations.
        if (!options.disableRefetch) {
          client.cache.evict({
            id: `Purchase:${data.removePurchase}`,
          });

          // Refetch only active FetchServicesOnTender queries for this tender
          // This ensures we make only ONE network call instead of multiple
          // for different parameter combinations
          client.refetchQueries({
            include: [FetchServicesOnTenderDocument],
            onQueryUpdated(observableQuery) {
              // Only refetch queries for this specific tender
              const variables = observableQuery.options.variables as { tenderId?: number } | undefined;
              return variables?.tenderId === Number(tenderID);
            },
          });
        }
      },
      refetchQueries: options.disableRefetch
        ? []
        : [
          {
            query: FetchTenderByIdDocument,
            variables: { id: Number(tenderID) },
          },
          {
            query: FetchPurchasesOnTenderDocument,
            variables: { tenderId: Number(tenderID) },
          },
        ],
    });

  return {
    removePurchaseMutation,
    loading,
    error,
    setDisableToast,
  };
}
